// Core packages
import { formatDate } from "@angular/common";
import {
  AfterViewInit, Component,
  Inject, OnDestroy, OnInit, ViewChild
} from "@angular/core";
import { FormControl } from "@angular/forms";

// Third party packages
import {
  MatPaginator, MatSort
} from "@angular/material";
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import * as FileSaver from "file-saver";
import { CookieService } from 'ngx-cookie-service';
import { ToastrService } from 'ngx-toastr';
import { combineLatest, merge, Subscription } from "rxjs";
import {
  debounceTime,
  distinctUntilChanged,
  startWith,
  tap
} from "rxjs/operators";

// Custom packages
import { AuthService } from 'src/app/modules/auth/auth.service';
import { PropertyTagNfcsService } from 'src/app/modules/properties/propertyTagNfcs.service';
import { ListSetting } from "src/app/shared/interfaces/list-setting.interface";
import { IUser } from 'src/app/shared/models/user/user.interface';
import { ConfigService } from 'src/app/shared/services/config.service';
import { HelperService } from 'src/app/shared/services/helper.service';
import { ListsService } from "src/app/shared/services/lists.service";
import { PropertyTagNfcsDataSource } from "src/app/shared/utils/data-sources/property-tag-nfcs-data-source";

@Component({
  selector: 'app-property-tag-nfc-list-dialog',
  templateUrl: './property-tag-nfc-list-dialog.component.html',
  styleUrls: ['./property-tag-nfc-list-dialog.component.scss']
})
export class PropertyTagNfcListDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  lang = this.translateService.currentLang || this.translateService.defaultLang;

  // BEGIN - Table properties
  displayedColumns: string[] = [
    "index",
    "uid",
    "name",
    "shortDescription",
    "description",
    "latitude",
    "longitude",
    "installationDate",
    "images",
    "createdAt",
    "updatedAt",
    "createdBy",
    "updatedBy",
  ];
  dataSource: PropertyTagNfcsDataSource;
  @ViewChild("sort1", { static: true }) sort: MatSort;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

  uidFilter: FormControl = new FormControl("");
  nameFilter: FormControl = new FormControl("");
  shortDescriptionFilter: FormControl = new FormControl("");
  descriptionFilter: FormControl = new FormControl("");
  latitudeFilter: FormControl = new FormControl("");
  longitudeFilter: FormControl = new FormControl("");
  installationDateFilter: FormControl = new FormControl("");
  imagesFilter: FormControl = new FormControl("");
  createdByFilter: FormControl = new FormControl("");
  updatedByFilter: FormControl = new FormControl("");
  createdAtFilter: FormControl = new FormControl("");
  updatedAtFilter: FormControl = new FormControl("");
  // END - Table properties
  
  dialogIsLoading = true;

  constructor(
    public dialogRef: MatDialogRef<PropertyTagNfcListDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,

    public configService: ConfigService,
    private cookieService: CookieService,
    private translateService: TranslateService,
    private propertyTagNfcsService: PropertyTagNfcsService,
    private authService: AuthService,
    private helperService: HelperService,
    private toastrService: ToastrService,
    private listsService: ListsService,
  ) {
    dialogRef.disableClose = true;
  }

  /**
   * Init component
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    // Get logged user data
    this.subscriptions.push(this.authService.loggedUser$.subscribe(res => this.loggedUser = res));

    // Get current lang
    this.lang = this.cookieService.get('lang') || this.configService.defaultLang;

    // Init data source
    this.dataSource = new PropertyTagNfcsDataSource(
      this.propertyTagNfcsService,
      this.helperService
    );

    // Subscribe to listsService and update this list settings
    this.subscribeToListsSettings("propertyTagNfcs");
  }

  
  /**
   * After view init logic
   *
   * @since 1.0.0
   */
  ngAfterViewInit(): void {
    // Load first set of data
    this.loadPage();

    // Reset pagination after sorting
    this.subscriptions.push(
      this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0))
    );

    // Once user change sorting OR change page
    // let's reload the page data
    this.subscriptions.push(
      merge(this.sort.sortChange, this.paginator.page)
        .pipe(
          tap(() => {
            this.loadPage();
          })
        )
        .subscribe()
    );

    // Handle server-side filtering through column-specific search
    // Why do we use "null as string"? --> @see https://github.com/ReactiveX/rxjs/issues/4772
    this.subscriptions.push(
      combineLatest([
        this.uidFilter.valueChanges.pipe(startWith(null as string)),
        this.nameFilter.valueChanges.pipe(startWith(null as string)),
        this.shortDescriptionFilter.valueChanges.pipe(startWith(null as string)),
        this.descriptionFilter.valueChanges.pipe(startWith(null as string)),
        this.latitudeFilter.valueChanges.pipe(startWith(null as string)),
        this.longitudeFilter.valueChanges.pipe(startWith(null as string)),
        this.installationDateFilter.valueChanges.pipe(startWith(null as string)),
        this.imagesFilter.valueChanges.pipe(startWith(null as string)),
        this.createdByFilter.valueChanges.pipe(startWith(null as string)),
        this.updatedByFilter.valueChanges.pipe(startWith(null as string)),
        this.createdAtFilter.valueChanges.pipe(startWith(null as string)),
        this.updatedAtFilter.valueChanges.pipe(startWith(null as string)),
      ])
        .pipe(
          debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
          distinctUntilChanged() // Eliminate duplicate values
        )
        .subscribe((res) => {
          // Load page only if at least one value is not null
          if (!res.every((value) => value === null)) {
            this.paginator.pageIndex = 0;
            this.loadPage();
          }
        })
    );
  }
  
  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());

    // Push current list settings to the listsService
    if (this.paginator) {
      this.listsService.setListSetting(
        "propertyTagNfcs",
        this.paginator.pageIndex.valueOf(),
        this.paginator.pageSize.valueOf(),
        {
          general: "", // this.input.nativeElement.value,
          uid: this.uidFilter.value,
          name: this.nameFilter.value,
          shortDescription: this.shortDescriptionFilter.value,
          description: this.descriptionFilter.value,
          latitude: this.latitudeFilter.value,
          longitude: this.longitudeFilter.value,
          installationDate: this.installationDateFilter.value,
          images: this.imagesFilter.value,
          createdBy: this.createdByFilter.value,
          updatedBy: this.updatedByFilter.value,
          createdAt: this.createdAtFilter.value,
          updatedAt: this.updatedAtFilter.value,
        }
      );
    }
  }

  /**
   * Load requested data page
   *
   * @since 1.0.0
   */
  loadPage(): void {
    const start = this.paginator.pageIndex * this.paginator.pageSize;
    const length = this.paginator.pageSize;
    const search = {
      general: "", // this.input.nativeElement.value,
      uid: this.uidFilter.value,
      name: this.nameFilter.value,
      shortDescription: this.shortDescriptionFilter.value,
      description: this.descriptionFilter.value,
      latitude: this.latitudeFilter.value,
      longitude: this.longitudeFilter.value,
      installationDate: this.installationDateFilter.value,
      images: this.imagesFilter.value,
      createdBy: this.createdByFilter.value,
      updatedBy: this.updatedByFilter.value,
      createdAt: this.createdAtFilter.value,
      updatedAt: this.updatedAtFilter.value,
    };
    this.dataSource.loadPropertyTagNfcItems(
      this.data.propertyId,
      start,
      length,
      this.sort.active,
      this.sort.direction,
      search
    );
  }

  /**
   * Export properties in a file with given format
   *
   * @since 1.0.0
   */
  onExport(): void {
    // If target is not provided, than just export all properties
    const date = formatDate(new Date(), "yyyyMMddhhmmss", "en-EN");
    const fileName = `${this.translateService.instant(
      "properties.dialog.propertyTagNfcList.exportPropertyFileName"
    )} ${date}`;
    this.subscriptions.push(
      this.propertyTagNfcsService.getExportPropertyComplete(this.data.propertyId).subscribe(
        (res) => {
          const fileBlob = res;
          const blob = new Blob([fileBlob], {
            type: "application/vnd.ms-excel",
          });
          FileSaver.saveAs(blob, `${fileName}.xlsx`);
        },
        (err) => {
          this.helperService.handleError(err);
        }
      )
    );
  }

  /**
   * Reset table's filters
   *
   * @since 1.0.0
   */
  onResetFilters(): void {
    // Reset lists service data
    this.listsService.setListSetting("", 0, 10, {});

    // Reset tables' filters
    // this.input.nativeElement.value = "";
    this.uidFilter.setValue(null);
    this.nameFilter.setValue(null);
    this.shortDescriptionFilter.setValue(null);
    this.descriptionFilter.setValue(null);
    this.latitudeFilter.setValue(null);
    this.longitudeFilter.setValue(null);
    this.installationDateFilter.setValue(null);
    this.imagesFilter.setValue(null);
    this.createdByFilter.setValue(null);
    this.updatedByFilter.setValue(null);
    this.createdAtFilter.setValue(null);
    this.updatedAtFilter.setValue(null);
  
    // Reset tables' sorting versus and column
    this.sort.direction = "asc";
    this.sort.active = "name";
    this.sort._stateChanges.next();

    // Reset paginator
    this.paginator.pageIndex = 0;
    this.paginator.pageSize = 10;

    // Reload table's data
    this.loadPage();
  }

  /**
   * Reset given filter value and reload the page
   *
   * @since 1.0.0
   */
  onResetFilter(filter: FormControl): void {
    filter.reset();
    this.loadPage();
  }
  
  /**
   * Reset general search input and dispath a 'keyup' event
   * so that the table re-render his rows
   *
   * @since 1.0.0
   */
  resetInput(): void {
    // this.input.nativeElement.value = "";
    const event = new KeyboardEvent("keyup");
    // this.input.nativeElement.dispatchEvent(event);
  }

  /**
   * Handle dialog closing
   *
   * @since 1.0.0
   *
   * @param [data] optional data passed to the caller component
   */
  onClose(data?: any): void {
    this.dialogRef.close(data);
  }

  /**
   * Subscribe to lists settings service to allow this list
   * to remember current filters once users navigate to another page.
   * In this way once user will come back to this page he will
   * have all previous settings (such as page number, page size, search, ...)
   *
   * @since 1.0.0
   */
  subscribeToListsSettings(name: string): void {
    this.listsService.enable();
    this.subscriptions.push(
      this.listsService.listsSettings$.subscribe((settings: ListSetting[]) => {
        if (settings.length) {
          settings.forEach((setting: ListSetting) => {
            if (setting.name === name) {
              if (setting.pageIndex) {
                this.paginator.pageIndex = setting.pageIndex;
              }
              if (setting.pageSize) {
                this.paginator.pageSize = setting.pageSize;
              }
              if (setting.search && setting.search.general) {
                // this.input.nativeElement.value = setting.search.general;
              }
              if (setting.search && setting.search.uid) {
                this.uidFilter.setValue(setting.search.uid);
              }
              if (setting.search && setting.search.name) {
                this.nameFilter.setValue(setting.search.name);
              }
              if (setting.search && setting.search.shortDescription) {
                this.shortDescriptionFilter.setValue(setting.search.shortDescription);
              }
              if (setting.search && setting.search.description) {
                this.descriptionFilter.setValue(setting.search.description);
              }
              if (setting.search && setting.search.latitude) {
                this.latitudeFilter.setValue(setting.search.latitude);
              }
              if (setting.search && setting.search.longitude) {
                this.longitudeFilter.setValue(setting.search.longitude);
              }
              if (setting.search && setting.search.installationDate) {
                this.installationDateFilter.setValue(setting.search.installationDate);
              }
              if (setting.search && setting.search.images) {
                this.imagesFilter.setValue(setting.search.images);
              }
              if (setting.search && setting.search.createdBy) {
                this.createdByFilter.setValue(setting.search.createdBy);
              }
              if (setting.search && setting.search.updatedBy) {
                this.updatedByFilter.setValue(setting.search.updatedBy);
              }
              if (setting.search && setting.search.createdAt) {
                this.createdAtFilter.setValue(setting.search.createdAt);
              }
              if (setting.search && setting.search.updatedAt) {
                this.updatedAtFilter.setValue(setting.search.updatedAt);
              }
            }
          });
        }
      })
    );
  }
}
